import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { usersAPI } from '../../api/users'
import { deliveriesAPI } from '../../api/deliveries'
import DashIcon from '../ui/DashIcon'
import DashAvatar from '../ui/DashAvatar'
import type { Delivery } from '../../types'

interface LivreurItem {
  _id: string
  name: string
  isOnline: boolean
  vehicle?: { type?: string; plate?: string }
}

interface ReassignedLivreur {
  _id: string
  name: string
}

interface ReassignCourierModalProps {
  parcel: Delivery | null
  isOpen: boolean
  onClose: () => void
  onReassigned: (livreur: ReassignedLivreur) => void
}

export default function ReassignCourierModal({ parcel, isOpen, onClose, onReassigned }: ReassignCourierModalProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  // Réinitialise la sélection à chaque ouverture / changement de colis ciblé
  useEffect(() => {
    if (isOpen) { setSelectedId(null); setError('') }
  }, [isOpen, parcel?._id])

  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isOpen, onClose])

  const { data, isLoading } = useQuery({
    queryKey: ['livreurs-reassign'],
    queryFn: () => usersAPI.getLivreurs({ limit: 50 }),
    enabled: isOpen,
    staleTime: 30_000,
  })

  // usersAPI.getLivreurs renvoie la réponse axios brute → .data (axios) .data (enveloppe backend { data, total, ... })
  const livreurs: LivreurItem[] = (data as { data?: { data?: LivreurItem[] } } | undefined)?.data?.data ?? []

  if (!isOpen || !parcel) return null

  const currentLivreurId = parcel.livreur?._id ?? parcel.livreur?.id
  const currentLivreurName = parcel.livreur?.name

  const fromCity = parcel.pickup?.address?.split(',').pop()?.trim()
  const toCity = parcel.dropoff?.address?.split(',').pop()?.trim() ?? parcel.dropoff?.address

  const handleConfirm = async () => {
    if (!selectedId) return
    const livreur = livreurs.find(l => l._id === selectedId)
    if (!livreur) return

    setSubmitting(true)
    setError('')
    try {
      await deliveriesAPI.assign(parcel._id, selectedId)
      onReassigned({ _id: livreur._id, name: livreur.name })
    } catch {
      setError("Erreur lors de l'assignation. Réessayez.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="reassign-overlay" onClick={onClose}>
      <div
        className="reassign-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="reassign-modal-title"
      >
        <div className="reassign-head">
          <div style={{ minWidth: 0 }}>
            <h3 id="reassign-modal-title" className="reassign-title">
              Ré-assigner — {parcel.trackingCode}
            </h3>
            <p className="reassign-subtitle">
              {parcel.dropoff?.contactName ?? '—'} · {fromCity ? `${fromCity} → ` : ''}{toCity ?? '—'}
            </p>
          </div>
          <button className="reassign-close" onClick={onClose} aria-label="Fermer">
            <DashIcon name="x" size={18} />
          </button>
        </div>

        <div className="reassign-current">
          Livreur actuel : <b>{currentLivreurName ?? 'Non assigné'}</b>
        </div>

        <div className="reassign-list">
          {isLoading ? (
            <div className="reassign-empty">Chargement des livreurs…</div>
          ) : livreurs.length === 0 ? (
            <div className="reassign-empty">Aucun livreur disponible</div>
          ) : (
            livreurs.map((l) => (
              <label key={l._id} className={`reassign-item${selectedId === l._id ? ' is-selected' : ''}`}>
                <input
                  type="radio"
                  name="reassign-livreur"
                  checked={selectedId === l._id}
                  onChange={() => setSelectedId(l._id)}
                  disabled={submitting}
                />
                <DashAvatar name={l.name} size={32} />
                <div className="reassign-item-info">
                  <div className="reassign-item-name">
                    {l.name}
                    {l._id === currentLivreurId && <span className="reassign-badge">Actuel</span>}
                  </div>
                  <div className="reassign-item-meta">
                    {l.isOnline ? 'En ligne' : 'Hors ligne'}
                    {l.vehicle?.type && ` · ${l.vehicle.type}`}
                  </div>
                </div>
              </label>
            ))
          )}
        </div>

        {error && <div className="reassign-error">{error}</div>}

        <div className="reassign-actions">
          <button className="btn btn-ghost" onClick={onClose} disabled={submitting}>Annuler</button>
          <button className="btn btn-primary" onClick={handleConfirm} disabled={!selectedId || submitting}>
            {submitting ? 'Assignation…' : 'Confirmer'}
          </button>
        </div>
      </div>
    </div>
  )
}
